/**
 * MEVIA v5.0 - Evaluation Cycles Management Panel
 * 
 * Allows admins to create, manage and close evaluation cycles.
 * Cycles are the foundation for tracking employee evolution over time.
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Calendar,
  Plus,
  Play,
  Archive,
  Trash2,
  Eye,
  CheckCircle,
  Clock,
  AlertTriangle,
  X,
  ChevronRight,
  Users,
  BarChart3,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import CycleEvolutionPanel from './CycleEvolutionPanel';
import logger from '../utils/logger';

// Bilingual texts
const TEXTS = {
  es: {
    title: 'Ciclos de Evaluación',
    subtitle: 'Gestiona los períodos de evaluación de tu empresa',
    newCycle: 'Nuevo Ciclo',
    noCycles: 'No hay ciclos de evaluación',
    noCyclesDesc: 'Crea tu primer ciclo para comenzar a evaluar a tus empleados.',
    createFirst: 'Crear Primer Ciclo',
    
    // Status
    status: {
      CONFIGURACION: 'Configuración',
      ACTIVO: 'Activo',
      CERRADO: 'Cerrado',
      ARCHIVADO: 'Archivado',
      VENCIDO: 'Vencido'
    },
    
    // Duration types
    duration: {
      ANUAL: 'Anual',
      SEMESTRAL: 'Semestral',
      TRIMESTRAL: 'Trimestral',
      CUSTOM: 'Personalizado'
    },
    
    // Form fields
    cycleName: 'Nombre del ciclo',
    cycleNamePlaceholder: 'Ej: Evaluación Anual 2026',
    durationType: 'Tipo de duración',
    startDate: 'Fecha de inicio',
    evaluationDeadline: 'Fecha límite de evaluaciones',
    adminNotes: 'Notas del administrador',
    adminNotesPlaceholder: 'Notas internas sobre este ciclo...',
    
    // Modules
    activeModules: 'Módulos activos',
    dimensions: 'Dimensiones culturales',
    enneagram: 'Eneagrama',
    nom035: 'NOM-035',
    pulse: 'Encuestas de Pulso',
    
    // Actions
    create: 'Crear',
    cancel: 'Cancelar',
    activate: 'Activar',
    close: 'Cerrar',
    archive: 'Archivar',
    delete: 'Eliminar',
    viewSnapshots: 'Ver Snapshots',
    viewCalendar: 'Ver Calendario',
    
    // Dialogs
    createCycleTitle: 'Crear Nuevo Ciclo',
    createCycleDesc: 'Configura un nuevo período de evaluación para tu empresa.',
    activateTitle: 'Activar Ciclo',
    activateDesc: '¿Estás seguro de que deseas activar este ciclo? Solo puede haber un ciclo activo a la vez.',
    closeTitle: 'Cerrar Ciclo',
    closeDesc: 'Al cerrar el ciclo se generará un snapshot inmutable de todos los empleados evaluados. Esta acción no se puede deshacer.',
    closeConfirm: '¿Confirmas que deseas cerrar este ciclo?',
    archiveTitle: 'Archivar Ciclo',
    archiveDesc: '¿Deseas archivar este ciclo? No aparecerá en la lista principal pero seguirá disponible para consultas.',
    deleteTitle: 'Eliminar Ciclo',
    deleteDesc: 'Solo se pueden eliminar ciclos en estado de Configuración. Esta acción no se puede deshacer.',
    
    // Messages
    cycleCreated: 'Ciclo creado exitosamente',
    cycleActivated: 'Ciclo activado exitosamente',
    cycleClosed: 'Ciclo cerrado exitosamente',
    cycleArchived: 'Ciclo archivado exitosamente',
    cycleDeleted: 'Ciclo eliminado exitosamente',
    errorCreating: 'Error al crear el ciclo',
    errorActivating: 'Error al activar el ciclo',
    errorClosing: 'Error al cerrar el ciclo',
    errorArchiving: 'Error al archivar el ciclo',
    errorDeleting: 'Error al eliminar el ciclo',
    errorLoading: 'Error al cargar los ciclos',
    
    // Info
    activeCycleWarning: 'Ya existe un ciclo activo. Ciérralo antes de crear uno nuevo.',
    employeesEvaluated: 'empleados evaluados',
    averageScore: 'Score MEVIA promedio',
    snapshots: 'snapshots generados',
    reviewDates: 'Fechas de revisión',
    
    // D9
    profileLocked: 'El perfil de empresa está bloqueado durante este ciclo. Para actualizarlo, cierra este ciclo e inicia uno nuevo.',
    startNewCycle: 'Iniciar Nuevo Ciclo',
    cycleExpired: 'Ciclo Vencido',
    decisionRequired: 'Se requiere una decisión para continuar.',
    hasSnapshot: 'Snapshot de perfil capturado',
    extendedBy: 'Extendido por',
    days: 'días',
    plansExpired: 'planes expirados',
    plansCompleted: 'planes cerrados',
    plansInProgress: 'planes en progreso'
  },
  en: {
    title: 'Evaluation Cycles',
    subtitle: 'Manage your company\'s evaluation periods',
    newCycle: 'New Cycle',
    noCycles: 'No evaluation cycles',
    noCyclesDesc: 'Create your first cycle to start evaluating your employees.',
    createFirst: 'Create First Cycle',
    
    // Status
    status: {
      CONFIGURACION: 'Configuration',
      ACTIVO: 'Active',
      CERRADO: 'Closed',
      ARCHIVADO: 'Archived',
      VENCIDO: 'Expired'
    },
    
    // Duration types
    duration: {
      ANUAL: 'Annual',
      SEMESTRAL: 'Semi-annual',
      TRIMESTRAL: 'Quarterly',
      CUSTOM: 'Custom'
    },
    
    // Form fields
    cycleName: 'Cycle name',
    cycleNamePlaceholder: 'E.g.: Annual Evaluation 2026',
    durationType: 'Duration type',
    startDate: 'Start date',
    evaluationDeadline: 'Evaluation deadline',
    adminNotes: 'Admin notes',
    adminNotesPlaceholder: 'Internal notes about this cycle...',
    
    // Modules
    activeModules: 'Active modules',
    dimensions: 'Cultural dimensions',
    enneagram: 'Enneagram',
    nom035: 'NOM-035',
    pulse: 'Pulse Surveys',
    
    // Actions
    create: 'Create',
    cancel: 'Cancel',
    activate: 'Activate',
    close: 'Close',
    archive: 'Archive',
    delete: 'Delete',
    viewSnapshots: 'View Snapshots',
    viewCalendar: 'View Calendar',
    
    // Dialogs
    createCycleTitle: 'Create New Cycle',
    createCycleDesc: 'Configure a new evaluation period for your company.',
    activateTitle: 'Activate Cycle',
    activateDesc: 'Are you sure you want to activate this cycle? Only one cycle can be active at a time.',
    closeTitle: 'Close Cycle',
    closeDesc: 'Closing the cycle will generate an immutable snapshot of all evaluated employees. This action cannot be undone.',
    closeConfirm: 'Do you confirm you want to close this cycle?',
    archiveTitle: 'Archive Cycle',
    archiveDesc: 'Do you want to archive this cycle? It won\'t appear in the main list but will remain available for queries.',
    deleteTitle: 'Delete Cycle',
    deleteDesc: 'Only cycles in Configuration status can be deleted. This action cannot be undone.',
    
    // Messages
    cycleCreated: 'Cycle created successfully',
    cycleActivated: 'Cycle activated successfully',
    cycleClosed: 'Cycle closed successfully',
    cycleArchived: 'Cycle archived successfully',
    cycleDeleted: 'Cycle deleted successfully',
    errorCreating: 'Error creating cycle',
    errorActivating: 'Error activating cycle',
    errorClosing: 'Error closing cycle',
    errorArchiving: 'Error archiving cycle',
    errorDeleting: 'Error deleting cycle',
    errorLoading: 'Error loading cycles',
    
    // Info
    activeCycleWarning: 'There is already an active cycle. Close it before creating a new one.',
    employeesEvaluated: 'employees evaluated',
    averageScore: 'Average MEVIA Score',
    snapshots: 'snapshots generated',
    reviewDates: 'Review dates',
    
    // D9
    profileLocked: 'Your company profile is locked for this cycle. To update it, close this cycle and start a new one.',
    startNewCycle: 'Start New Cycle',
    cycleExpired: 'Cycle Expired',
    decisionRequired: 'A decision is required to continue.',
    hasSnapshot: 'Profile snapshot captured',
    extendedBy: 'Extended by',
    days: 'days',
    plansExpired: 'plans expired',
    plansCompleted: 'plans closed',
    plansInProgress: 'plans in progress'
  }
};

// Status badge colors
const STATUS_COLORS = {
  CONFIGURACION: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  ACTIVO: 'bg-green-100 text-green-800 border-green-300',
  CERRADO: 'bg-blue-100 text-blue-800 border-blue-300',
  ARCHIVADO: 'bg-gray-100 text-gray-600 border-gray-300',
  VENCIDO: 'bg-red-100 text-red-800 border-red-300'
};

const STATUS_ICONS = {
  CONFIGURACION: Clock,
  ACTIVO: Play,
  CERRADO: CheckCircle,
  ARCHIVADO: Archive,
  VENCIDO: AlertTriangle
};

const EvaluationCyclesPanel = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const texts = TEXTS[language] || TEXTS.es;
  
  // State
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSnapshotsDialog, setShowSnapshotsDialog] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [creating, setCreating] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  
  // Form state for new cycle
  const [formData, setFormData] = useState({
    nombre: '',
    duracion_tipo: 'ANUAL',
    fecha_inicio: '',
    fecha_limite_evaluaciones: '',
    notas_admin: '',
    modulos_activos: {
      dimensiones: true,
      eneagrama: false,
      nom035: false,
      pulso: false
    }
  });

  useEffect(() => {
    if (user?.empresa_id) {
      loadCycles();
    }
  }, [user, includeArchived]);

  const loadCycles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `/v5/ciclos/${user.empresa_id}?include_archived=${includeArchived}`
      );
      if (response.data.success) {
        setCycles(response.data.data.cycles);
      }
    } catch (error) {
      logger.error('Error loading cycles:', error);
      toast.error(texts.errorLoading);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCycle = async () => {
    if (!formData.nombre || !formData.fecha_inicio || !formData.fecha_limite_evaluaciones) {
      return;
    }
    
    setCreating(true);
    try {
      const response = await axios.post(`/v5/ciclos/${user.empresa_id}`, {
        ...formData,
        fecha_inicio: new Date(formData.fecha_inicio).toISOString(),
        fecha_limite_evaluaciones: new Date(formData.fecha_limite_evaluaciones).toISOString()
      });
      
      if (response.data.success) {
        toast.success(texts.cycleCreated);
        setShowCreateDialog(false);
        resetForm();
        loadCycles();
      }
    } catch (error) {
      logger.error('Error creating cycle:', error);
      toast.error(error.response?.data?.detail || texts.errorCreating);
    } finally {
      setCreating(false);
    }
  };

  const handleActivateCycle = async () => {
    if (!selectedCycle) return;
    
    try {
      const response = await axios.post(`/v5/ciclos/${selectedCycle.ciclo_id}/activar`);
      if (response.data.success) {
        toast.success(texts.cycleActivated);
        setShowActivateDialog(false);
        loadCycles();
      }
    } catch (error) {
      logger.error('Error activating cycle:', error);
      toast.error(error.response?.data?.detail || texts.errorActivating);
    }
  };

  const handleCloseCycle = async () => {
    if (!selectedCycle) return;
    
    try {
      const response = await axios.post(`/v5/ciclos/${selectedCycle.ciclo_id}/cerrar?confirmar=true`);
      if (response.data.success) {
        toast.success(`${texts.cycleClosed} - ${response.data.data.snapshots_generados} ${texts.snapshots}`);
        setShowCloseDialog(false);
        loadCycles();
      }
    } catch (error) {
      logger.error('Error closing cycle:', error);
      toast.error(error.response?.data?.detail || texts.errorClosing);
    }
  };

  const handleArchiveCycle = async () => {
    if (!selectedCycle) return;
    
    try {
      const response = await axios.post(`/v5/ciclos/${selectedCycle.ciclo_id}/archivar`);
      if (response.data.success) {
        toast.success(texts.cycleArchived);
        setShowArchiveDialog(false);
        loadCycles();
      }
    } catch (error) {
      logger.error('Error archiving cycle:', error);
      toast.error(error.response?.data?.detail || texts.errorArchiving);
    }
  };

  const handleDeleteCycle = async () => {
    if (!selectedCycle) return;
    
    try {
      const response = await axios.delete(`/v5/ciclos/${selectedCycle.ciclo_id}`);
      if (response.data.success) {
        toast.success(texts.cycleDeleted);
        setShowDeleteDialog(false);
        loadCycles();
      }
    } catch (error) {
      logger.error('Error deleting cycle:', error);
      toast.error(error.response?.data?.detail || texts.errorDeleting);
    }
  };

  const handleViewSnapshots = async (cycle) => {
    setSelectedCycle(cycle);
    try {
      const response = await axios.get(`/v5/ciclos/${cycle.ciclo_id}/snapshots?limit=100`);
      if (response.data.success) {
        setSnapshots(response.data.data.snapshots);
        setShowSnapshotsDialog(true);
      }
    } catch (error) {
      logger.error('Error loading snapshots:', error);
      toast.error(texts.errorLoading);
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      duracion_tipo: 'ANUAL',
      fecha_inicio: '',
      fecha_limite_evaluaciones: '',
      notas_admin: '',
      modulos_activos: {
        dimensiones: true,
        eneagrama: false,
        nom035: false,
        pulso: false
      }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const hasActiveCycle = cycles.some(c => c.estado === 'ACTIVO' || c.estado === 'VENCIDO');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">{texts.title}</h2>
          <p className="text-sm text-gray-500">{texts.subtitle}</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              className="rounded border-gray-300"
            />
            {language === 'es' ? 'Mostrar archivados' : 'Show archived'}
          </label>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="new-cycle-btn">
            <Plus className="w-4 h-4 mr-2" />
            {texts.newCycle}
          </Button>
        </div>
      </div>

      {/* Active Cycle Warning */}
      {hasActiveCycle && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">{texts.activeCycleWarning}</p>
        </div>
      )}

      {/* D9: Profile locked banner */}
      {hasActiveCycle && (
        <div data-testid="profile-locked-banner" className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          <div>
            <p className="text-sm text-blue-800">{texts.profileLocked}</p>
          </div>
        </div>
      )}

      {/* D9: Expired cycle banner */}
      {cycles.some(c => c.estado === 'VENCIDO') && (
        <div data-testid="cycle-expired-banner" className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800 font-medium">{texts.cycleExpired}: {texts.decisionRequired}</p>
        </div>
      )}

      {/* Cycles List */}
      {cycles.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Calendar className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{texts.noCycles}</h3>
            <p className="text-gray-500 mb-6">{texts.noCyclesDesc}</p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              {texts.createFirst}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {cycles.map((cycle) => {
            const StatusIcon = STATUS_ICONS[cycle.estado];
            return (
              <Card key={cycle.ciclo_id} className="hover:shadow-md transition-shadow" data-testid={`cycle-card-${cycle.ciclo_id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{cycle.nombre}</h3>
                        <Badge className={`${STATUS_COLORS[cycle.estado]} border`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {texts.status[cycle.estado]}
                        </Badge>
                        <Badge variant="outline">
                          {texts.duration[cycle.duracion_tipo]}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-6 text-sm text-gray-600 mt-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(cycle.fecha_inicio)} - {formatDate(cycle.fecha_limite_evaluaciones)}
                        </span>
                        
                        {cycle.empleados_evaluados_snapshot !== null && (
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {cycle.empleados_evaluados_snapshot} {texts.employeesEvaluated}
                          </span>
                        )}
                        
                        {cycle.mevia_score_snapshot !== null && (
                          <span className="flex items-center gap-1">
                            <BarChart3 className="w-4 h-4" />
                            {texts.averageScore}: {cycle.mevia_score_snapshot.toFixed(1)}
                          </span>
                        )}
                        
                        {cycle.has_company_snapshot && (
                          <Badge variant="outline" className="text-xs border-teal-300 text-teal-700">
                            {texts.hasSnapshot}
                          </Badge>
                        )}
                        
                        {cycle.extension_days && (
                          <Badge variant="outline" className="text-xs border-blue-300 text-blue-700">
                            {texts.extendedBy} {cycle.extension_days} {texts.days}
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {/* Actions based on status */}
                      {cycle.estado === 'CONFIGURACION' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setSelectedCycle(cycle); setShowActivateDialog(true); }}
                            data-testid={`activate-btn-${cycle.ciclo_id}`}
                          >
                            <Play className="w-4 h-4 mr-1" />
                            {texts.activate}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setSelectedCycle(cycle); setShowDeleteDialog(true); }}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-btn-${cycle.ciclo_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      
                      {cycle.estado === 'ACTIVO' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setSelectedCycle(cycle); setShowCloseDialog(true); }}
                          data-testid={`close-btn-${cycle.ciclo_id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          {texts.close}
                        </Button>
                      )}
                      
                      {cycle.estado === 'CERRADO' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewSnapshots(cycle)}
                            data-testid={`snapshots-btn-${cycle.ciclo_id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            {texts.viewSnapshots}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setSelectedCycle(cycle); setShowArchiveDialog(true); }}
                            data-testid={`archive-btn-${cycle.ciclo_id}`}
                          >
                            <Archive className="w-4 h-4 mr-1" />
                            {texts.archive}
                          </Button>
                        </>
                      )}
                      
                      {cycle.estado === 'ARCHIVADO' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewSnapshots(cycle)}
                          data-testid={`snapshots-btn-${cycle.ciclo_id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          {texts.viewSnapshots}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Cycle Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{texts.createCycleTitle}</DialogTitle>
            <DialogDescription>{texts.createCycleDesc}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="nombre">{texts.cycleName}</Label>
              <Input
                id="nombre"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder={texts.cycleNamePlaceholder}
                data-testid="cycle-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="duracion">{texts.durationType}</Label>
              <Select
                value={formData.duracion_tipo}
                onValueChange={(value) => setFormData({ ...formData, duracion_tipo: value })}
              >
                <SelectTrigger data-testid="duration-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ANUAL">{texts.duration.ANUAL}</SelectItem>
                  <SelectItem value="SEMESTRAL">{texts.duration.SEMESTRAL}</SelectItem>
                  <SelectItem value="TRIMESTRAL">{texts.duration.TRIMESTRAL}</SelectItem>
                  <SelectItem value="CUSTOM">{texts.duration.CUSTOM}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fecha_inicio">{texts.startDate}</Label>
                <Input
                  id="fecha_inicio"
                  type="date"
                  value={formData.fecha_inicio}
                  onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
                  data-testid="start-date-input"
                />
              </div>
              <div>
                <Label htmlFor="fecha_limite">{texts.evaluationDeadline}</Label>
                <Input
                  id="fecha_limite"
                  type="date"
                  value={formData.fecha_limite_evaluaciones}
                  onChange={(e) => setFormData({ ...formData, fecha_limite_evaluaciones: e.target.value })}
                  data-testid="deadline-input"
                />
              </div>
            </div>
            
            <div>
              <Label>{texts.activeModules}</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <label className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={formData.modulos_activos.dimensiones}
                    onChange={(e) => setFormData({
                      ...formData,
                      modulos_activos: { ...formData.modulos_activos, dimensiones: e.target.checked }
                    })}
                    className="rounded"
                  />
                  <span className="text-sm">{texts.dimensions}</span>
                </label>
                <label className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={formData.modulos_activos.eneagrama}
                    onChange={(e) => setFormData({
                      ...formData,
                      modulos_activos: { ...formData.modulos_activos, eneagrama: e.target.checked }
                    })}
                    className="rounded"
                  />
                  <span className="text-sm">{texts.enneagram}</span>
                </label>
                <label className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={formData.modulos_activos.nom035}
                    onChange={(e) => setFormData({
                      ...formData,
                      modulos_activos: { ...formData.modulos_activos, nom035: e.target.checked }
                    })}
                    className="rounded"
                  />
                  <span className="text-sm">{texts.nom035}</span>
                </label>
                <label className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={formData.modulos_activos.pulso}
                    onChange={(e) => setFormData({
                      ...formData,
                      modulos_activos: { ...formData.modulos_activos, pulso: e.target.checked }
                    })}
                    className="rounded"
                  />
                  <span className="text-sm">{texts.pulse}</span>
                </label>
              </div>
            </div>
            
            <div>
              <Label htmlFor="notas">{texts.adminNotes}</Label>
              <Input
                id="notas"
                value={formData.notas_admin}
                onChange={(e) => setFormData({ ...formData, notas_admin: e.target.value })}
                placeholder={texts.adminNotesPlaceholder}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              {texts.cancel}
            </Button>
            <Button 
              onClick={handleCreateCycle} 
              disabled={creating || !formData.nombre || !formData.fecha_inicio || !formData.fecha_limite_evaluaciones}
              data-testid="create-cycle-submit"
            >
              {creating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              {texts.create}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activate Dialog */}
      <Dialog open={showActivateDialog} onOpenChange={setShowActivateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{texts.activateTitle}</DialogTitle>
            <DialogDescription>{texts.activateDesc}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActivateDialog(false)}>
              {texts.cancel}
            </Button>
            <Button onClick={handleActivateCycle} data-testid="confirm-activate-btn">
              <Play className="w-4 h-4 mr-2" />
              {texts.activate}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Dialog */}
      <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              {texts.closeTitle}
            </DialogTitle>
            <DialogDescription className="space-y-2">
              <p>{texts.closeDesc}</p>
              <p className="font-medium text-gray-700">{texts.closeConfirm}</p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloseDialog(false)}>
              {texts.cancel}
            </Button>
            <Button onClick={handleCloseCycle} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-close-btn">
              <CheckCircle className="w-4 h-4 mr-2" />
              {texts.close}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Dialog */}
      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{texts.archiveTitle}</DialogTitle>
            <DialogDescription>{texts.archiveDesc}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArchiveDialog(false)}>
              {texts.cancel}
            </Button>
            <Button onClick={handleArchiveCycle} data-testid="confirm-archive-btn">
              <Archive className="w-4 h-4 mr-2" />
              {texts.archive}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              {texts.deleteTitle}
            </DialogTitle>
            <DialogDescription>{texts.deleteDesc}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              {texts.cancel}
            </Button>
            <Button onClick={handleDeleteCycle} variant="destructive" data-testid="confirm-delete-btn">
              <Trash2 className="w-4 h-4 mr-2" />
              {texts.delete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Snapshots Dialog */}
      <Dialog open={showSnapshotsDialog} onOpenChange={setShowSnapshotsDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {texts.viewSnapshots} - {selectedCycle?.nombre}
            </DialogTitle>
            <DialogDescription>
              {snapshots.length} {texts.snapshots}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2 py-4">
            {snapshots.map((snapshot) => (
              <div 
                key={snapshot.snapshot_id} 
                className="flex items-center justify-between p-3 border rounded-lg bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    snapshot.tipo === 'EMPRESA' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {snapshot.tipo === 'EMPRESA' ? (
                      <BarChart3 className="w-5 h-5" />
                    ) : (
                      <Users className="w-5 h-5" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {snapshot.user_nombre || (snapshot.tipo === 'EMPRESA' ? (language === 'es' ? 'Promedio Empresa' : 'Company Average') : '-')}
                    </p>
                    <p className="text-sm text-gray-500">
                      {snapshot.tipo === 'EMPLEADO' && snapshot.nivel_mevia && (
                        <Badge variant="outline" className="mr-2">{snapshot.nivel_mevia}</Badge>
                      )}
                      {formatDate(snapshot.fecha_snapshot)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-teal-600">
                    {snapshot.mevia_score?.toFixed(1) || '-'}
                  </p>
                  <p className="text-xs text-gray-500">MEVIA Score</p>
                </div>
              </div>
            ))}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSnapshotsDialog(false)}>
              <X className="w-4 h-4 mr-2" />
              {texts.cancel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* D9 Phase 6: Cycle Evolution Panel */}
      {user?.empresa_id && cycles.length >= 2 && (
        <CycleEvolutionPanel empresaId={user.empresa_id} />
      )}
    </div>
  );
};

export default EvaluationCyclesPanel;
